import React, { useEffect, useMemo, useState } from "react";
import { auth, db, storage } from "../firebaseConfig";
import {
  collection,
  doc,
  getDocs,
  serverTimestamp,
  setDoc,
  getDoc,
} from "firebase/firestore";
import {
  getDownloadURL,
  ref as sRef,
  uploadBytes,
} from "firebase/storage";
import "./CrearAnuncioGeneralPage.css";

const FORMATOS = [
  { value: "individual", label: "Home principal" },
  { value: "cuadrado", label: "Cuadrado entre productos" },
  { value: "reel", label: "Rectangular / reel entre productos" },
];

const MAX_CATEGORIAS = 3;
const MAX_VIDEO_MB = 40;

function cleanPhone(value) {
  return (value || "").replace(/[^\d]/g, "");
}

function buildWhatsappUrl(phone) {
  const clean = cleanPhone(phone);
  if (!clean) return "";
  return `https://wa.me/${clean}`;
}

function dataUrlToBlob(dataUrl) {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)?.[1] || "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new Blob([u8arr], { type: mime });
}

function loadImageFromFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = reader.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function resizeSquareImage(file, size = 800, quality = 0.88) {
  const img = await loadImageFromFile(file);

  const side = Math.min(img.width, img.height);
  const sx = (img.width - side) / 2;
  const sy = (img.height - side) / 2;

  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);

  const dataUrl = canvas.toDataURL("image/jpeg", quality);
  return dataUrlToBlob(dataUrl);
}

function getPreviewImage(formato) {
  switch (formato) {
    case "home":
      return "/add_home.jpg";
    case "cuadrado":
      return "/add_cuadrado.jpg";
    case "reel":
      return "/add_reel.jpg";
    default:
      return "/add_cuadrado.jpg";
  }
}

function getPrecioTexto(precioData, formato) {
  if (!precioData) return "";
  if (formato === "home") return precioData.precioHome || "";
  if (formato === "cuadrado") return precioData.precioCuadrado || "";
  if (formato === "reel") return precioData.precioRell || "";
  return "";
}

export default function CrearAnuncioGeneralPage() {
  const uid = auth.currentUser?.uid || "";

  const [loadingCats, setLoadingCats] = useState(true);
  const [categorias, setCategorias] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msgOk, setMsgOk] = useState("");
  const [msgErr, setMsgErr] = useState("");

  const [titulo, setTitulo] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [telefono, setTelefono] = useState("");
  const [direccionStr, setDireccionStr] = useState("");
  const [formato, setFormato] = useState("cuadrado");

  const [categoriasSel, setCategoriasSel] = useState([]);

  const [clickUrl, setClickUrl] = useState("");
  const [usarWhatsapp, setUsarWhatsapp] = useState(false);
  const [whatsapp, setWhatsapp] = useState("");

  const [imagenFile, setImagenFile] = useState(null);
  const [videoFile, setVideoFile] = useState(null);

  const [showPreview, setShowPreview] = useState(false);

  const [precioData, setPrecioData] = useState(null);
  const [loadingPrecio, setLoadingPrecio] = useState(true);

  useEffect(() => {
    cargarCategorias();
    cargarPrecioAnuncio();
  }, []);

  async function cargarPrecioAnuncio() {
    try {
      setLoadingPrecio(true);
      const ref = doc(db, "anuncios", "precio");
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setPrecioData(snap.data());
      } else {
        setPrecioData(null);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPrecio(false);
    }
  }

  async function cargarCategorias() {
    try {
      setLoadingCats(true);
      const snap = await getDocs(collection(db, "categorias"));
      const lista = snap.docs.map((d) => {
        const data = d.data() || {};
        return {
          id: d.id,
          nombre: data.nombre || data.titulo || d.id,
        };
      });
      lista.sort((a, b) => a.nombre.localeCompare(b.nombre, "es"));
      setCategorias(lista);
    } catch (e) {
      console.error(e);
      setMsgErr("Error al cargar categorías");
    } finally {
      setLoadingCats(false);
    }
  }

  function toggleCategoria(id) {
    setCategoriasSel((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id);
      if (prev.length >= MAX_CATEGORIAS) return prev;
      return [...prev, id];
    });
  }

  const finalClickUrl = useMemo(() => {
    if (usarWhatsapp) return buildWhatsappUrl(whatsapp);
    return clickUrl.trim();
  }, [usarWhatsapp, whatsapp, clickUrl]);

  const precioTexto = useMemo(() => {
    return getPrecioTexto(precioData, formato);
  }, [precioData, formato]);

  const canSave = useMemo(() => {
    return (
      !!uid &&
      titulo.trim().length > 0 &&
      descripcion.trim().length > 0 &&
      direccionStr.trim().length > 0 &&
      !!imagenFile &&
      !!formato &&
      categoriasSel.length > 0 &&
      categoriasSel.length <= 3 &&
      (usarWhatsapp ? cleanPhone(whatsapp).length >= 10 : finalClickUrl.length > 0)
    );
  }, [
    uid,
    titulo,
    descripcion,
    direccionStr,
    imagenFile,
    formato,
    categoriasSel,
    usarWhatsapp,
    whatsapp,
    finalClickUrl,
  ]);

  async function handleGuardar() {
    try {
      setBusy(true);
      setMsgOk("");
      setMsgErr("");

      if (!uid) throw new Error("Debes iniciar sesión");
      if (!canSave) throw new Error("Completa los campos requeridos");

      if (videoFile) {
        const mb = videoFile.size / (1024 * 1024);
        if (mb > MAX_VIDEO_MB) {
          throw new Error(`El video supera ${MAX_VIDEO_MB} MB`);
        }
      }

      const anuncioRef = doc(collection(db, "anunciosPagados"));
      const anuncioId = anuncioRef.id;

      const img800Blob = await resizeSquareImage(imagenFile, 800, 0.88);
      const thumb250Blob = await resizeSquareImage(imagenFile, 250, 0.85);

      const basePath = `usuarios/${uid}/anuncios/${anuncioId}`;

      const imgRef = sRef(storage, `${basePath}/imagen.jpg`);
      const thumbRef = sRef(storage, `${basePath}/thumbnail.jpg`);

      await uploadBytes(imgRef, img800Blob, { contentType: "image/jpeg" });
      await uploadBytes(thumbRef, thumb250Blob, { contentType: "image/jpeg" });

      const imagenUrl = await getDownloadURL(imgRef);
      const thumbnailUrl = await getDownloadURL(thumbRef);

      let videoUrl = null;
      let esVideo = false;

      if (videoFile) {
        const videoRef = sRef(storage, `${basePath}/video.mp4`);
        await uploadBytes(videoRef, videoFile, {
          contentType: videoFile.type || "video/mp4",
        });
        videoUrl = await getDownloadURL(videoRef);
        esVideo = true;
      }

      const anuncioData = {
        id: anuncioId,
        usuarioId: uid,

        titulo: titulo.trim(),
        descripcion: descripcion.trim(),

        telefono: telefono.trim() || null,
        direccionStr: direccionStr.trim(),

        imagenUrl,
        thumbnailUrl,
        videoUrl,
        esVideo,

        activo: true,
        aprobado: false,

        formato,
        categorias: categoriasSel,

        clickUrl: finalClickUrl || null,
        irPantalla: true,
        pathFirebase: null,

        whatsapp: usarWhatsapp ? cleanPhone(whatsapp) : null,

        orden: 0,

        impresionesTotal: 0,
        impresionesPorDia: {},

        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        startAt: null,
        endAt: null,
      };

      await setDoc(anuncioRef, anuncioData);

      setMsgOk("Anuncio guardado correctamente");
      limpiarFormulario();
    } catch (e) {
      console.error(e);
      setMsgErr(e.message || "Error al guardar anuncio");
    } finally {
      setBusy(false);
    }
  }

  function limpiarFormulario() {
    setTitulo("");
    setDescripcion("");
    setTelefono("");
    setDireccionStr("");
    setFormato("cuadrado");
    setCategoriasSel([]);
    setClickUrl("");
    setUsarWhatsapp(false);
    setWhatsapp("");
    setImagenFile(null);
    setVideoFile(null);
  }

  return (
    <div className="crear-anuncio-page">
      <div className="crear-anuncio-wrap">
        <div className="crear-anuncio-header">
          <h1>Crear anuncio</h1>
          <p>Selecciona la zona, sube tu imagen y completa los datos.</p>
        </div>

        <div className="crear-anuncio-card">
          <div className="zonas-header">
            <div className="zonas-title">Zona del anuncio</div>
            <button
              type="button"
              className="ver-preview-btn"
              onClick={() => setShowPreview(true)}
            >
              Ver dónde estará mi anuncio
            </button>
          </div>

          <div className="zonas-tabs">
            {FORMATOS.map((item) => (
              <button
                key={item.value}
                type="button"
                className={`zona-tab ${formato === item.value ? "active" : ""}`}
                onClick={() => setFormato(item.value)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="precio-box">
            <div className="precio-label">Precio</div>
            <div className="precio-value">
              {loadingPrecio ? "Cargando..." : precioTexto || "No definido"}
            </div>
            <div className="precio-msg">
              {precioData?.mensajeAnuncio || ""}
            </div>
          </div>

          <div className="form-grid-2">
            <div>
              <label className="field-label">Título</label>
              <input
                className="field-input"
                value={titulo}
                onChange={(e) => setTitulo(e.target.value)}
                placeholder="Ej. Casa en venta al norte"
              />
            </div>

            <div>
              <label className="field-label">Teléfono</label>
              <input
                className="field-input"
                value={telefono}
                onChange={(e) => setTelefono(e.target.value)}
                placeholder="625..."
              />
            </div>
          </div>

          <div>
            <label className="field-label">Descripción</label>
            <textarea
              className="field-textarea"
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Describe tu anuncio"
            />
          </div>

          <div>
            <label className="field-label">Dirección</label>
            <input
              className="field-input"
              value={direccionStr}
              onChange={(e) => setDireccionStr(e.target.value)}
              placeholder="Colonia, calle, referencia..."
            />
          </div>

          <div className="form-grid-2">
            <div>
              <label className="field-label">Imagen principal</label>
              <input
                className="field-file"
                type="file"
                accept="image/*"
                onChange={(e) => setImagenFile(e.target.files?.[0] || null)}
              />
              <div className="field-help">
                Se recorta en cuadrado y se generan versiones de 800 px y 250 px.
              </div>
            </div>

            <div>
              <label className="field-label">Video opcional</label>
              <input
                className="field-file"
                type="file"
                accept="video/mp4,video/*"
                onChange={(e) => setVideoFile(e.target.files?.[0] || null)}
              />
              <div className="field-help">
                Recomendado: MP4. Por ahora se sube directo a Firebase Storage.
              </div>
            </div>
          </div>

          <div>
            <label className="field-label">Categorías (máximo 3)</label>

            {loadingCats ? (
              <div className="simple-box">Cargando categorías...</div>
            ) : (
              <div className="tags-wrap">
                {categorias.map((cat) => {
                  const active = categoriasSel.includes(cat.id);
                  const disabled = !active && categoriasSel.length >= MAX_CATEGORIAS;

                  return (
                    <button
                      key={cat.id}
                      type="button"
                      className={`tag-btn ${active ? "active" : ""}`}
                      disabled={disabled}
                      onClick={() => toggleCategoria(cat.id)}
                    >
                      {cat.nombre}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="switch-row">
            <div>
              <div className="switch-title">Enviar a WhatsApp</div>
              <div className="field-help">
                Si se activa, se oculta la URL y se genera automáticamente el clickUrl.
              </div>
            </div>

            <label className="default-switch">
              <input
                type="checkbox"
                checked={usarWhatsapp}
                onChange={(e) => setUsarWhatsapp(e.target.checked)}
              />
              <span />
            </label>
          </div>

          {usarWhatsapp ? (
            <div>
              <label className="field-label">Número de WhatsApp</label>
              <input
                className="field-input"
                value={whatsapp}
                onChange={(e) => setWhatsapp(e.target.value)}
                placeholder="526251234567"
              />
              <div className="field-help">
                URL generada: {finalClickUrl || "sin generar"}
              </div>
            </div>
          ) : (
            <div>
              <label className="field-label">URL de destino</label>
              <input
                className="field-input"
                value={clickUrl}
                onChange={(e) => setClickUrl(e.target.value)}
                placeholder="https://..."
              />
            </div>
          )}

          <div className="footer-actions">
            <button
              className="save-btn"
              disabled={busy || !canSave}
              onClick={handleGuardar}
            >
              {busy ? "Guardando..." : "Guardar anuncio"}
            </button>
          </div>

          {!!msgOk && <div className="ok-box">{msgOk}</div>}
          {!!msgErr && <div className="err-box">{msgErr}</div>}
        </div>
      </div>

      {showPreview && (
        <div className="preview-overlay" onClick={() => setShowPreview(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="preview-close"
              onClick={() => setShowPreview(false)}
            >
              ×
            </button>

            <div className="preview-title">Vista previa del anuncio</div>

            <div className="preview-image-wrap">
              <img
                src={getPreviewImage(formato)}
                alt="Vista previa anuncio"
                className="preview-image"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}